import { useEffect, useState } from 'react';
import { api } from '../lib/api';

type Config = {
  _id?: string;
  timezone: string;
  bufferMinutes: number;
  defaultDurationMinutes: number;
  bookingUrlSlug: string;
  bookingPageEnabled: boolean;
  meetingLinkTemplate?: string;
  workHours: { dayOfWeek: number; start: string; end: string }[];
};

const EMPTY: Config = {
  timezone: 'America/Denver',
  bufferMinutes: 15,
  defaultDurationMinutes: 30,
  bookingUrlSlug: '',
  bookingPageEnabled: false,
  meetingLinkTemplate: '',
  workHours: Array.from({ length: 5 }, (_, i) => ({ dayOfWeek: i + 1, start: '09:00', end: '17:00' })),
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function CalendarSettings() {
  const [cfg, setCfg] = useState<Config>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get('/api/calendar-config/me').then(({ data }) => {
      if (data.config) setCfg({ ...EMPTY, ...data.config });
    });
  }, []);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const { data } = await api.put('/api/calendar-config/me', {
        timezone: cfg.timezone,
        bufferMinutes: cfg.bufferMinutes,
        defaultDurationMinutes: cfg.defaultDurationMinutes,
        bookingUrlSlug: cfg.bookingUrlSlug,
        bookingPageEnabled: cfg.bookingPageEnabled,
        meetingLinkTemplate: cfg.meetingLinkTemplate || '',
        workHours: cfg.workHours,
      });
      setCfg({ ...EMPTY, ...data.config });
      setSaved(true);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  function updateRow(idx: number, patch: Partial<Config['workHours'][number]>) {
    const next = cfg.workHours.slice();
    next[idx] = { ...next[idx], ...patch };
    setCfg({ ...cfg, workHours: next });
  }

  function addRow() {
    setCfg({ ...cfg, workHours: [...cfg.workHours, { dayOfWeek: 1, start: '09:00', end: '17:00' }] });
  }

  function removeRow(idx: number) {
    setCfg({ ...cfg, workHours: cfg.workHours.filter((_, i) => i !== idx) });
  }

  return (
    <main className="min-h-screen bg-dgal-ink text-white">
      <header className="border-b border-dgal-navy-2 px-8 py-4">
        <div className="text-dgrp-red text-sm uppercase tracking-[0.2em] font-semibold">Calendar Settings</div>
      </header>
      <section className="max-w-3xl mx-auto px-8 py-10">
        <form onSubmit={save} className="grid gap-5">
          <label className="grid gap-2">
            <span className="text-xs uppercase tracking-wider text-dgal-mist">Timezone (IANA)</span>
            <input
              value={cfg.timezone}
              onChange={(e) => setCfg({ ...cfg, timezone: e.target.value })}
              className="bg-dgal-navy/40 border border-dgal-navy-2 rounded px-3 py-2"
            />
          </label>

          <div className="grid grid-cols-2 gap-4">
            <label className="grid gap-2">
              <span className="text-xs uppercase tracking-wider text-dgal-mist">Buffer (min)</span>
              <input
                type="number"
                value={cfg.bufferMinutes}
                onChange={(e) => setCfg({ ...cfg, bufferMinutes: Number(e.target.value) })}
                className="bg-dgal-navy/40 border border-dgal-navy-2 rounded px-3 py-2"
              />
            </label>
            <label className="grid gap-2">
              <span className="text-xs uppercase tracking-wider text-dgal-mist">Default duration (min)</span>
              <input
                type="number"
                value={cfg.defaultDurationMinutes}
                onChange={(e) => setCfg({ ...cfg, defaultDurationMinutes: Number(e.target.value) })}
                className="bg-dgal-navy/40 border border-dgal-navy-2 rounded px-3 py-2"
              />
            </label>
          </div>

          <label className="grid gap-2">
            <span className="text-xs uppercase tracking-wider text-dgal-mist">Booking page slug</span>
            <input
              value={cfg.bookingUrlSlug}
              onChange={(e) => setCfg({ ...cfg, bookingUrlSlug: e.target.value })}
              placeholder="your-name"
              className="bg-dgal-navy/40 border border-dgal-navy-2 rounded px-3 py-2"
            />
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={cfg.bookingPageEnabled}
              onChange={(e) => setCfg({ ...cfg, bookingPageEnabled: e.target.checked })}
              className="accent-dgrp-red"
            />
            <span className="text-sm">Enable public booking page</span>
          </label>

          <label className="grid gap-2">
            <span className="text-xs uppercase tracking-wider text-dgal-mist">Meeting link template</span>
            <input
              value={cfg.meetingLinkTemplate || ''}
              onChange={(e) => setCfg({ ...cfg, meetingLinkTemplate: e.target.value })}
              placeholder="https://zoom.us/j/123?ref={appointmentId}"
              className="bg-dgal-navy/40 border border-dgal-navy-2 rounded px-3 py-2"
            />
          </label>

          <div>
            <div className="text-xs uppercase tracking-wider text-dgal-mist mb-2">Work hours</div>
            <ul className="space-y-2">
              {cfg.workHours.map((wh, i) => (
                <li key={i} className="flex items-center gap-2">
                  <select
                    value={wh.dayOfWeek}
                    onChange={(e) => updateRow(i, { dayOfWeek: Number(e.target.value) })}
                    className="bg-dgal-navy/40 border border-dgal-navy-2 rounded px-2 py-1 text-sm"
                  >
                    {DAYS.map((d, idx) => (
                      <option key={d} value={idx}>{d}</option>
                    ))}
                  </select>
                  <input
                    value={wh.start}
                    onChange={(e) => updateRow(i, { start: e.target.value })}
                    placeholder="09:00"
                    className="bg-dgal-navy/40 border border-dgal-navy-2 rounded px-2 py-1 text-sm w-20"
                  />
                  <span className="text-dgal-mist">to</span>
                  <input
                    value={wh.end}
                    onChange={(e) => updateRow(i, { end: e.target.value })}
                    placeholder="17:00"
                    className="bg-dgal-navy/40 border border-dgal-navy-2 rounded px-2 py-1 text-sm w-20"
                  />
                  <button type="button" onClick={() => removeRow(i)} className="text-xs text-dgal-mist hover:text-red-400 ml-2">
                    Remove
                  </button>
                </li>
              ))}
            </ul>
            <button type="button" onClick={addRow} className="mt-2 text-xs text-dg-text-2 hover:text-dgrp-red">
              + Add row
            </button>
          </div>

          {error && <div className="text-red-400 text-sm">{error}</div>}
          {saved && <div className="text-dgrp-red text-sm">Saved.</div>}
          <button
            type="submit"
            disabled={saving}
            className="self-start px-4 py-2 rounded bg-dgrp-red text-white text-sm font-semibold hover:bg-dgrp-red-hover"
          >
            {saving ? 'Saving…' : 'Save settings'}
          </button>
        </form>
      </section>
    </main>
  );
}
